import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pushEvent } from '@/hooks/useAnalytics';
import { galleryMockData, localizeGalleryItem, type GalleryItem } from '@/pages/gallery/data/galleryData';
import GalleryCardImage from '@/pages/gallery/components/GalleryCardImage';

interface InspirationCardProps {
  item: GalleryItem;
  index: number;
  onClick: () => void;
}

function InspirationCard({ item, index, onClick }: InspirationCardProps) {
  const { i18n } = useTranslation();
  const displayItem = localizeGalleryItem(item, i18n.language);

  const formatCount = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  return (
    <div
      className="relative rounded-xl overflow-hidden cursor-pointer bg-white active:scale-[0.97] transition-transform"
      style={{ aspectRatio: displayItem.ratio === '4:3' ? '4/3' : displayItem.ratio === '1:1' ? '1/1' : '3/4' }}
      onClick={onClick}
    >
      <GalleryCardImage
        item={displayItem}
        alt={displayItem.prompt.slice(0, 20)}
        width={320}
        eager={index < 4}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

      {/* 底部信息 */}
      <div className="absolute bottom-0 left-0 right-0 p-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full overflow-hidden border border-white/30 flex-shrink-0">
            <img src={item.authorAvatar} alt="" className="w-full h-full object-cover object-top" />
          </div>
          <span className="text-white/80 text-[10px] truncate max-w-[60px]">{displayItem.authorName}</span>
        </div>
        <div className="flex items-center gap-1">
          <i className="ri-heart-line text-white/60 text-[10px]" />
          <span className="text-white/70 text-[10px]">{formatCount(displayItem.likes)}</span>
        </div>
      </div>
    </div>
  );
}

const filterTabs = [
  { id: 'all', labelKey: 'gallery_filter_all', activeColor: '#9333EA' },
  { id: 'text2img', labelKey: 'gallery_filter_text2img', activeColor: '#9333EA' },
  { id: 'img2img', labelKey: 'gallery_filter_img2img', activeColor: '#9333EA' },
  { id: 'edit', labelKey: 'gallery_filter_edit', activeColor: '#9333EA' },
  { id: 'restore', labelKey: 'gallery_filter_restore', activeColor: '#9333EA' },
  { id: 'tryon', labelKey: 'gallery_filter_tryon', activeColor: '#9333EA' },
];

export default function InspirationGrid() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredItems = activeFilter === 'all'
    ? galleryMockData.slice(0, 8)
    : galleryMockData.filter((item) => item.toolType === activeFilter).slice(0, 8);

  const handleItemClick = (item: GalleryItem) => {
    pushEvent('home_inspiration_click', undefined, { itemId: item.id, toolType: item.toolType });
    setSelectedItem(item);
    setShowDetail(true);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeFilter]);

  return (
    <div className="px-5">
      {/* 标题 */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[17px] font-bold text-gray-900 tracking-tight">{t('gallery_title')}</h2>
        <button
          onClick={() => navigate('/gallery')}
          className="flex items-center gap-0.5 text-xs text-gray-400 font-medium cursor-pointer active:scale-95 transition-transform whitespace-nowrap"
        >
          <span>{t('home_slideMore')}</span>
          <i className="ri-arrow-right-s-line text-gray-400" />
        </button>
      </div>

      {/* 标签栏 */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mb-3"
        style={{ scrollbarWidth: 'none' }}
      >
        {filterTabs.map((tab) => {
          const isActive = activeFilter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all active:scale-95 ${
                isActive ? 'text-white' : 'text-gray-500 bg-white border border-gray-100'
              }`}
              style={
                isActive
                  ? { background: '#9333EA' }
                  : { border: '1px solid rgba(0,0,0,0.06)' }
              }
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {/* 图片网格 — 2列 */}
      <div ref={scrollRef} className="grid grid-cols-2 gap-2.5">
        {filteredItems.map((item, index) => (
          <InspirationCard
            key={item.id}
            item={item}
            index={index}
            onClick={() => handleItemClick(item)}
          />
        ))}
      </div>

      {/* 详情弹窗 — 使用 Portal 渲染到 body */}
      {showDetail && selectedItem && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowDetail(false)}
          />
          <div className="relative bg-white w-full max-w-md mx-auto sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            <button
              onClick={() => setShowDetail(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center cursor-pointer active:scale-90 transition-transform"
            >
              <i className="ri-close-line text-lg" />
            </button>
            <div className="relative w-full flex-shrink-0 bg-gray-100" style={{ maxHeight: '55vh' }}>
              <img
                src={localizeGalleryItem(selectedItem, i18n.language).imageUrl}
                alt={localizeGalleryItem(selectedItem, i18n.language).prompt}
                className="w-full object-cover object-top"
                style={{ maxHeight: '55vh' }}
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  <img src={selectedItem.authorAvatar} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <span className="text-sm font-semibold text-gray-800">{localizeGalleryItem(selectedItem, i18n.language).authorName}</span>
                <span className="text-xs text-gray-400 ml-auto">{localizeGalleryItem(selectedItem, i18n.language).createdAt}</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-3">{localizeGalleryItem(selectedItem, i18n.language).prompt}</p>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={() => {
                    setShowDetail(false);
                    navigate(`/generate?prompt=${encodeURIComponent(localizeGalleryItem(selectedItem, i18n.language).prompt)}`);
                  }}
                  className="py-3 rounded-xl text-white text-sm font-semibold cursor-pointer active:scale-95 transition-all whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
                >
                  <i className="ri-magic-line mr-1" />
                  {t('gallery_usePrompt')}
                </button>
                <button
                  onClick={() => {
                    setShowDetail(false);
                    navigate('/gallery');
                  }}
                  className="py-3 rounded-xl text-sm font-semibold cursor-pointer active:scale-95 transition-all whitespace-nowrap"
                  style={{ background: 'rgba(255,107,157,0.08)', color: '#FF6B9D', border: '1px solid rgba(255,107,157,0.15)' }}
                >
                  {t('gallery_title')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
